<template>
  <div
    class="w-full h-full flex flex-col pt-4 pl-3 pb-0! tablet-h:p-[14px] tablet-h:pr-[64px] desktop:pr-[14px]"
    :style="componentStyles"
  >
    <div
      class="font-semibold font-primary text-[18px] leading-[30px] text-gray-800 mb-5 tablet-h:pl-10"
    >
      Archived copyrights
    </div>
    <div ref="tableRef" class="overflow-hidden">
      <ArchiveTable :items="items" @sort="handleSort" @scroll="handleScroll" />
    </div>
  </div>
  <div ref="sidebar">
    <ArchiveSidebar
      :selected-item="selectedItem"
      :total="result?.archivedFiles.total"
      class="hidden tablet-h:flex h-[calc(100vh-61px)]! tablet-h:absolute top-0 right-0 bottom-0 desktop:static"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import {
  SearchSort,
  useArchivedFilesQuery,
  type ArchivedFile,
} from '@/shared/graphql/legacy/generated'
import ArchiveTable from '@/features/explorer/components/ArchiveTable.vue'
import { useAvailableHeight } from '@/shared/composables/useAvailableHeight'
import { useHead } from '@unhead/vue'
import { Table } from '@dacr/ui'
import ArchiveSidebar from '@/features/explorer/components/ArchiveSidebar.vue'
import { TableTypes } from '@/shared/types/index'

useHead({
  title: 'Archived copyrights - Dacr',
})

const sortBy = ref<SearchSort>(SearchSort.NameAsc)

const variables = computed(() => ({
  sortBy: sortBy.value,
}))

const { result, loading, fetchMore } = useArchivedFilesQuery(variables, {
  fetchPolicy: 'cache-and-network',
})

const { availableHeight } = useAvailableHeight()
const table = Table.useTable(TableTypes.Archive)

const tableRef = ref<HTMLElement>()
const sidebar = ref<HTMLElement>()

const componentStyles = computed(() => ({
  height: `${availableHeight.value}px`,
}))

const selectedItem = computed(() =>
  items.value?.find((item) => table.selectedRows.value[0] === item.id)
)

const handleSort = (sort: [string, 'asc' | 'desc']) => {
  if (sort[0] === 'name' && sort[1] === 'asc') {
    sortBy.value = SearchSort.NameAsc
  }
  if (sort[0] === 'name' && sort[1] === 'desc') {
    sortBy.value = SearchSort.NameDesc
  }
  if (sort[0] === 'timestamp' && sort[1] === 'asc') {
    sortBy.value = SearchSort.TimestampAsc
  }
  if (sort[0] === 'timestamp' && sort[1] === 'desc') {
    sortBy.value = SearchSort.TimestampDesc
  }
}

const items = computed(
  () => result.value?.archivedFiles.items as ArchivedFile[] | undefined
)

const fetchNextPage = () => {
  if (result.value?.archivedFiles.next && !loading.value) {
    fetchMore({
      variables: {
        next: result.value.archivedFiles.next,
      },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData

        return {
          __typename: 'Query',
          archivedFiles: {
            ...previousData.archivedFiles,
            items: [
              ...previousData.archivedFiles.items,
              ...newData.archivedFiles.items,
            ],
            next: newData.archivedFiles.next,
          },
        }
      },
    })
  }
}

const handleScroll = (event: Event) => {
  const element = event.target as HTMLElement
  const elementHeight = element.scrollHeight
  const scrollPos = element.clientHeight + element.scrollTop

  if (scrollPos / elementHeight >= 0.5) {
    fetchNextPage()
  }
}

const handleOutsideClick = (event: Event) => {
  if (
    event.target === tableRef.value ||
    event.target === sidebar.value ||
    tableRef.value?.contains(event.target as HTMLElement) ||
    sidebar.value?.contains(event.target as HTMLElement)
  ) {
    return
  }

  table.resetSelection()
}

onMounted(() => {
  window.addEventListener('click', handleOutsideClick)
})

onBeforeUnmount(() => {
  window.removeEventListener('click', handleOutsideClick)
  table.resetSelection()
})
</script>
